import { Checkbox, FormControlLabel } from '@material-ui/core';
import cx from 'classnames';
import {
  icon_calendar,
  icon_circle_alert,
  icon_paper_plane,
  icon_pencil_square,
  icon_user,
} from 'components/base/images';
import MuiButton from 'components/common/button/MuiButton';
import DateConverter from 'components/common/convert/DateConverter';
import MuiWrapper from 'components/common/input/MuiWrapper';
import MultidiePlate from 'components/common/multidie/MultidiePlate';
import TeethSvgV2 from 'components/common/teeth/TeethSvgV2';
import { T } from 'components/common/text';
import CustomSpan from 'components/common/text/CustomSpan';
import { ProjectContext } from 'contexts/project/ProjectContext';
import useCheckSetInput from 'lib/hooks/useCheckSetInput';
import useInput from 'lib/hooks/useInput';
import { capitalize } from 'lib/library';
import { projectProcessFlagList } from 'lib/mapper';
import { useDidUpdateEffect } from 'lib/utils';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useShallowAppSelector } from 'store/hooks';
import { isClinicUserSelector } from 'store/modules/user';
import styled from 'styled-components';
import { color } from 'styles/utils';

interface Tooth {
  number: string;
  indicationIdx: string;
}

interface ProjectCardProps {
  isRework?: boolean;
  data: {
    clientName: string;
    firstName: string;
    lastName: string;
    identification: string;
    dueDate: number;
    enrollDate: number;
    projectCode: string;
    projectId: string;
    // projectIdx: number;
    projectName: string;
    projectStatus: number;
    reworkOrder: number;
    stage: number;
    info: string;
    teeth: Tooth[];
    bridgeList: string[];
    syncStatus: number;
    hasNewEvent: boolean;
    dueDiffDay: number;
    senderMemo: string;
    ownerUserStatus: number | null;
    dueDayWarning: number;
    isMultidie: boolean;
  };
  onCloseProjectListModal?: () => void;
  onDeleteProject?: (pcode: string) => void;
}

const ProjectCard = ({
  isRework = false,
  data,
  onCloseProjectListModal,
  onDeleteProject,
}: ProjectCardProps) => {
  const {
    language,
    teethData,
    reworkProjectSuccess,
    deleteProjectSuccess,
    copyProjectSuccess,
    isClinicUser,
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    teethData: state.util.teeth,
    reworkProjectSuccess: state.project.reworkProject.success,
    deleteProjectSuccess: state.project.deleteProject.success,
    copyProjectSuccess: state.project.copyProject.success,
    isClinicUser: isClinicUserSelector(state),
  }));
  const history = useHistory();
  const { copyOptionData } = useContext(ProjectContext);
  const [isDelete, setIsDelete] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [stageIndex, setStageIndex] = useState(0);

  const [isTitleHover, setIsTitleHover] = useState(false);
  const [isMemoHover, setIsMemoHover] = useState(false);
  const [isMemoModal, setIsMemoModal] = useState(false);

  const [teethDataArr, setTeethDataArr] = useState<any[]>([]);
  const teeth = useInput([]);
  const tooth = useInput({});

  const copyOptionObj = useCheckSetInput(new Set(['INFO']));

  const dateWarning = data.dueDiffDay > 3 || isClinicUser ? false : true;

  const projectStatusList = [
    { id: 0, name: 'create', icon: icon_pencil_square },
    { id: 1, name: 'invited', icon: icon_paper_plane },
    // { id: 2, name: 'sync', icon: icon_cloud_upload },
  ];

  const handleClickDeleteProject = (projectCode: string) => {
    if (onDeleteProject) onDeleteProject(projectCode);
  };

  const handleProjectDuplicate = useCallback(
    (id: string) => {
      copyOptionData.setValue([...copyOptionObj.value]);
      history.push(`/project/copy/${id}`);
    },
    [copyOptionObj.value],
  );

  useEffect(() => {
    if (data?.stage === null) {
      setStageIndex(-1);
    } else if (data?.stage === 0) {
      setStageIndex(1);
    } else if (data?.stage === 4) {
      setStageIndex(5);
    } else {
      setStageIndex(data?.stage);
    }
  }, [data]);

  useEffect(() => {
    if (data.teeth.length <= 0) return;
    // NOTE: TeethSvgV2에 Thumnail 사용시, 필요한 데이터 추가
    const newTeethData: Tooth[] = data.teeth?.map(item => {
      return { ...item, idx: item.indicationIdx };
    });
    setTeethDataArr([...newTeethData]);
    teeth.setValue([...newTeethData]);
  }, [data.teeth]);

  useDidUpdateEffect(() => {
    if (deleteProjectSuccess) setIsDelete(false);
  }, [deleteProjectSuccess]);

  useEffect(() => {
    if (!isTitleHover && !isMemoHover) {
      setIsMemoModal(false);
    } else {
      setIsMemoModal(true);
    }
  }, [isTitleHover, isMemoHover]);

  if (!data) return null;
  return (
    <StyledProjectCard
      data-component-name="ProjectCard"
      className="projectCard__container"
      isRework={isRework}
    >
      <div
        className={cx('projectCard__memo_wrapper', { active: isMemoModal })}
        onMouseEnter={() => {
          setIsMemoHover(true);
        }}
        onMouseLeave={() => {
          setIsMemoHover(false);
        }}
      >
        <div className={cx('projectCard__memo_box')}>
          <div className="projectCard__memo_title">
            <T>PROJECT_MEMO</T>
          </div>
          <div className="projectCard__memo_contents">{data.senderMemo}</div>
        </div>
      </div>

      {!isDelete && !isCopy && (
        <div
          className="projectCard__container_wrapper"
          onClick={() => history.push(`/project/detail/${data.projectCode}`)}
        >
          <div className="projectCard__top_container">
            <div className="projectCard__top_box">
              {/* {!!dateWarning && data.stage !== 7 && (
                <div className="projectCard__top_warning_label"></div>
              )} */}

              {!!data.dueDayWarning && !isClinicUser && (
                <div className="projectCard__top_warning_label"></div>
              )}

              <div className="projectCard__top">
                <span
                  className="projectCard__symbol"
                  style={{
                    backgroundColor: projectProcessFlagList.find(
                      (item: any) => item.id === stageIndex,
                    )?.color,
                  }}
                />
                <T>{projectProcessFlagList.find((item: any) => item.id === stageIndex)?.code}</T>
              </div>

              <span className="projectCard__enroll_date">
                {language === 'en' ? (
                  <DateConverter
                    language={language}
                    timestamp={data?.enrollDate}
                    format="MMM.DD YYYY HH:mm"
                  />
                ) : (
                  <DateConverter
                    language={language}
                    timestamp={data?.enrollDate}
                    format="YYYY.MM.DD HH:mm"
                  />
                )}
              </span>
            </div>
          </div>

          <div className="projectCard__content">
            {data.hasNewEvent && (
              <div className="projectCard__new_event">
                <img src={icon_circle_alert} alt="alert" />
              </div>
            )}
            <div className="projectCard__info">
              <div className="projectCard__info_item">
                <div>
                  <div className="projectCard__by">
                    {(data?.projectStatus === null || data?.projectStatus === 1) && (
                      <>
                        <img
                          src={
                            projectStatusList[
                              data?.projectStatus === null ? 1 : data?.projectStatus
                            ].icon
                          }
                          alt="upload icon"
                        />
                        {capitalize(
                          projectStatusList[data?.projectStatus === null ? 1 : data?.projectStatus]
                            .name,
                        )}
                        {data?.syncStatus === 1 && ' (Sync)'}
                      </>
                    )}
                  </div>
                  <div
                    className="projectCard__title"
                    onMouseEnter={() => {
                      setIsTitleHover(true);
                    }}
                    onMouseLeave={() => {
                      setIsTitleHover(false);
                    }}
                  >
                    {data.projectName}
                  </div>
                </div>
                <div>
                  <div
                    className={cx('projectCard__deadline', {
                      dateWarning: dateWarning && data.stage !== 7,
                    })}
                  >
                    <img src={icon_calendar} alt="calendar icon" />
                    {!!data.dueDate ? moment.unix(data.dueDate).format('YYYY-MM-DD') : 'NONE'}
                  </div>
                  <div className="projectCard__client_name">
                    <img src={icon_user} alt="user icon" />
                    {isClinicUser
                      ? `${data.firstName} ${data.lastName}${
                          !!data.identification ? data.identification : ''
                        }`
                      : data.clientName}
                  </div>
                </div>
              </div>

              <div className="projectCard__info_item btn_box">
                <div className="button_box">
                  {data?.projectStatus === 0 && (
                    <MuiButton
                      fullWidth
                      variant="outlined"
                      color="primary"
                      className="sm border-radius-round btn_box_duplicate"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setIsCopy(true);
                      }}
                    >
                      <T>GLOBAL_DUPLICATE</T>
                    </MuiButton>
                  )}
                  {(data?.projectStatus === 0 || data?.ownerUserStatus !== 0) && (
                    <MuiButton
                      fullWidth
                      variant="outlined"
                      color="primary"
                      className="sm border-radius-round btn_box_delete"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setIsDelete(true);
                      }}
                    >
                      <T>GLOBAL_DELETE</T>
                    </MuiButton>
                  )}
                </div>
              </div>
            </div>
            <div className="projectCard__thumbnail">
              {!!data?.isMultidie ? (
                <MultidiePlate isEdit={false} tooth={tooth} teeth={teeth} isListPage />
              ) : (
                <TeethSvgV2
                  teeth={{ value: teethDataArr }}
                  bridge={{ value: data.bridgeList }}
                  numbering={{ value: 0 }}
                  hiddenToothText
                />
              )}
            </div>
          </div>
        </div>
      )}
      {!!isDelete && (
        <div className="projectCard__action_container delete">
          <div className="projectCard__action_pcode">
            <p>{data.projectName}</p>
          </div>
          <div className="projectCard__action_content">
            <div className="projectCard__action_info">
              <p className="projectCard__action_info_title">
                <CustomSpan fontSize={20} fontWeight={700}>
                  <T>PROJECT_DELETE_QUESTION</T>
                </CustomSpan>
              </p>
              <p className="projectCard__action_info_detail">
                <T>PROJECT_DELETE_QUESTION_DETAIL</T>
              </p>
            </div>
            <div className="projectCard__action_button_box">
              <MuiWrapper>
                <MuiButton
                  fullWidth
                  variant="contained"
                  className="sm"
                  onClick={() => setIsDelete(false)}
                >
                  <T>GLOBAL_NO</T>
                </MuiButton>
              </MuiWrapper>
              <MuiWrapper>
                <MuiButton
                  fullWidth
                  variant="contained"
                  color="primary"
                  className="sm"
                  onClick={() => handleClickDeleteProject(data.projectCode)}
                >
                  <T>GLOBAL_YES</T>
                </MuiButton>
              </MuiWrapper>
            </div>
          </div>
        </div>
      )}

      {!!isCopy && (
        <div className="projectCard__action_container copy">
          <div className="projectCard__action_pcode">
            <p>{data.projectName}</p>
          </div>
          <div className="projectCard__action_content">
            <div className="projectCard__action_info">
              <p className="projectCard__action_info_title">
                <CustomSpan fontSize={18} fontWeight={500}>
                  <T>PROJECT_COPY_TEXT</T>
                </CustomSpan>
              </p>
              <div className="projectCard__action_info_detail">
                <FormControlLabel
                  className="contents_check_box_wrapper"
                  control={
                    <MuiWrapper>
                      <Checkbox
                        checked={copyOptionObj.value.has('FILE')}
                        onChange={() => {
                          copyOptionObj.onChange({ value: 'FILE' });
                        }}
                        className="checkbox_control_checkbox"
                        name={'FILE'}
                        color="primary"
                        icon={<div className="custom_checkbox"></div>}
                      />
                    </MuiWrapper>
                  }
                  label={
                    <CustomSpan fontSize={14}>
                      <T>DUPLICATE_INCLUDE_FILE</T>
                    </CustomSpan>
                  }
                />
              </div>
            </div>
            <div className="projectCard__action_button_box">
              <MuiWrapper>
                <MuiButton
                  fullWidth
                  variant="contained"
                  className="sm"
                  onClick={() => setIsCopy(false)}
                >
                  <T>GLOBAL_NO</T>
                </MuiButton>
              </MuiWrapper>
              <MuiWrapper>
                <MuiButton
                  fullWidth
                  variant="contained"
                  color="primary"
                  className="sm"
                  onClick={() => {
                    handleProjectDuplicate(data.projectCode);
                  }}
                >
                  <T>GLOBAL_DUPLICATE</T>
                </MuiButton>
              </MuiWrapper>
            </div>
          </div>
        </div>
      )}
    </StyledProjectCard>
  );
};

const StyledProjectCard = styled.div<{ isRework: boolean }>`
  position: relative;
  width: 390px;
  height: 330px;
  .projectCard__memo_wrapper {
    position: absolute;
    left: 0;
    bottom: 240px;
    display: none;
    &.active {
      display: block;
    }
    padding-bottom: 10px;
    z-index: 100;
    background-color: transparent;
    .projectCard__memo_box {
      line-height: 1.71;
      letter-spacing: -0.28px;
      background-color: #ffffff;
      width: 310px;
      padding: 20px;
      background-color: #fefefe;
      border-radius: 10px;
      box-shadow: 10px 10px 20px 0 rgba(0, 0, 0, 0.1);
      .projectCard__memo_title {
        line-height: 22px;
        font-size: 18px;
        margin-bottom: 20px;
      }
      .projectCard__memo_contents {
        height: 150px;
        overflow-y: overlay;
        text-overflow: ellipsis;
        word-break: break-all;
        white-space: break-spaces;
        padding-right: 15px;
        margin-right: -15px;
      }
    }
  }

  .projectCard__container_wrapper {
    max-height: 330px;
    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 10px;
      box-shadow: inset 0px 2px 3px rgba(0, 0, 0, 0.16);
      background-color: ${({ isRework }) => (isRework ? color.navy_blue : '#fcfcfc')};
    }
    &:hover {
      cursor: pointer;
      .projectCard__content {
        /* background-color: #fafafa; */
        background-color: #f4f4f4;
      }
    }
  }

  .projectCard__top_box {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    font-size: 12px;
    color: ${({ isRework }) => isRework && 'white'};
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    overflow: hidden;
    .projectCard__top_warning_label {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      background-color: ${color.stage_urgent};
      height: 3px;
    }

    .projectCard__top {
      display: flex;
      align-items: center;
      line-height: 1;
      .projectCard__symbol {
        display: inline-block;
        margin-right: 12px;
        width: 14px;
        height: 14px;
        border-radius: 2px;
      }
    }
    .projectCard__enroll_date {
      color: #858997;
    }
  }

  .projectCard__fold_btn {
    margin: -12px -5px;
    padding: 0;
  }
  .projectCard__rework_box {
    position: relative;
    padding: 15px 20px;

    font-size: 15px;
    color: white;
    font-weight: 300;
    line-height: 1.4;

    .dot {
      display: inline-block;
      position: relative;
      margin-right: 10px;
      width: 18px;
      height: 18px;
      background-color: white;
    }
    .projectCard__rework_title {
      margin-bottom: 10px;
    }
    .projectCard__rework_content {
      .projectCard__rework_content_item {
        display: flex;
        padding: 2px 0;
        .projectCard__rework_content_description {
          height: 42px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      }
      .projectCard__rework_content_select_btn_box {
        margin-top: 20px;
        .button {
          height: 30px;
          width: 50%;
        }
      }
    }
  }

  .projectCard__content {
    height: 292px;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    position: relative;
    padding: 27px 20px 25px 20px;
    background-color: white;
    border-radius: 10px;
    box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.16);

    .projectCard__new_event {
      position: absolute;
      top: 15px;
      right: 15px;
      img {
        width: 30px;
        height: 30px;
      }
    }
    .projectCard__info {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: calc(100% - 140px - 15px);
      font-size: 15px;

      .projectCard__info_item {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        &:not(.btn_box) {
          height: 220px;
        }
        &.btn_box {
          .button {
            height: 30px;
          }
        }
        .button_box {
          display: flex;
          column-gap: 10px;
          .btn_box_duplicate {
            width: 100px;
          }
          .btn_box_delete {
            width: 70px;
          }
        }
      }

      .projectCard__title {
        margin-bottom: 20px;
        max-height: 100px;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 5;
        -webkit-box-orient: vertical;
        &:hover {
          text-decoration: underline;
        }
      }

      .projectCard__by,
      .projectCard__deadline,
      .projectCard__client_name {
        display: flex;
        align-items: center;
        > img {
          margin-right: 10px;
        }
      }
      .projectCard__by {
        font-size: 13px;
        height: 15px;
        line-height: 15px;
        color: ${color.blue};
        margin-bottom: 13px;
      }
      .projectCard__deadline {
        margin-bottom: 14px;
        &.dateWarning {
          color: ${color.stage_urgent};
        }
      }
      .projectCard__client_name {
        margin-bottom: 30px;
      }
    }
    .projectCard__thumbnail {
      width: 140px;
      margin: -5px 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .projectCard__action_container {
    width: 100%;
    height: 330px;
    background-color: #00155e;
    border-radius: 10px;
    padding: 0 20px;
    text-align: center;
    .projectCard__action_pcode {
      padding: 20px 0 15px 0;
      border-bottom: 1px solid #b5b7c1;
      p {
        line-height: 24px;
        font-size: 18px;
        font-weight: 500;
        color: #b5b7c1;
      }
    }
    .projectCard__action_content {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      .projectCard__action_info {
        padding: 30px 0 45px 0;
        .projectCard__action_info_title {
          line-height: 27px;
          color: #ffffff;
          margin-bottom: 19px;
        }
        .projectCard__action_info_detail {
          line-height: 20px;
          font-size: 12px;
          font-weight: 400;
          color: #ffffff;
          word-break: break-all;
          white-space: break-spaces;
        }
      }
      .projectCard__action_button_box {
        display: flex;
        column-gap: 10px;
      }
    }
  }

  .contents_check_box_wrapper {
    height: 40px;
    &.all {
      width: 100%;
      height: 40px;
    }

    .checkbox_control_checkbox {
      width: 42px;
      height: 42px;
    }
  }
  .custom_checkbox {
    width: 20px;
    height: 20px;
    border: 1px solid #e5e7ed;
    border-radius: 4px;
  }
`;

export default React.memo(ProjectCard);
